import { useSyncExternalStore, useEffect } from 'react'
import { SANA_REBRAND_ENABLED } from '~/common/feature_flags'
import { usePublicFeatureFlagLocalStorage } from '~/hooks/useFeatureFlag'

export const SANA_REBRAND_DEV_OVERRIDE_KEY = 'sana_rebrand_dev_override'

/** `?vr_panel=off` → legacy, `?vr_panel=on` → rebrand. Used by VrLayout side-by-side. */
function readVrPanelOverride(): boolean | null {
  if (typeof window === 'undefined') return null
  const panel = new URLSearchParams(window.location.search).get('vr_panel')
  if (panel === 'on') return true
  if (panel === 'off') return false
  return null
}

function useVrPanelOverride(): boolean | null {
  return useSyncExternalStore(
    () => () => {},
    readVrPanelOverride,
    () => null
  )
}

type DevOverrideListener = () => void
const devOverrideListeners = new Set<DevOverrideListener>()

function readDevOverride(): boolean | null {
  if (typeof window === 'undefined') return null
  const value = window.localStorage.getItem(SANA_REBRAND_DEV_OVERRIDE_KEY)
  if (value === 'on') return true
  if (value === 'off') return false
  return null
}

function subscribeDevOverride(onStoreChange: DevOverrideListener) {
  devOverrideListeners.add(onStoreChange)
  const onStorage = (event: StorageEvent) => {
    if (event.key === SANA_REBRAND_DEV_OVERRIDE_KEY || event.key === null) onStoreChange()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    devOverrideListeners.delete(onStoreChange)
    window.removeEventListener('storage', onStorage)
  }
}

/**
 * Force the effective rebrand theme on/off for this browser, or pass `null` so
 * the tenant flag wins. Notifies live subscribers. Only applied on `/vr` pages,
 * and only while the tenant `sana_rebrand_enabled` flag is ON. Product pages
 * clear a stored value. Cannot preview rebrand when the flag is off.
 */
export function setSanaRebrandDevOverride(_value: boolean | null | ((prev: 'on' | 'off' | null) => boolean | null)) {
  if (typeof window === 'undefined') return
  let value: boolean | null
  if (_value !== null && typeof _value === 'function') {
    value = _value(window.localStorage.getItem(SANA_REBRAND_DEV_OVERRIDE_KEY) as 'on' | 'off' | null)
  } else {
    value = _value
  }
  if (value === null) {
    window.localStorage.removeItem(SANA_REBRAND_DEV_OVERRIDE_KEY)
  } else {
    window.localStorage.setItem(SANA_REBRAND_DEV_OVERRIDE_KEY, value ? 'on' : 'off')
  }
  devOverrideListeners.forEach(listener => listener())
}

function useDevOverride(): boolean | null {
  return useSyncExternalStore(subscribeDevOverride, readDevOverride, () => null)
}

/**
 * Tenant flag only. Ignores VR panel and localStorage overrides.
 * Used to decide whether the /vr showcase theme toggle should appear.
 */
export function useIsSanaRebrandFlagOn(): boolean {
  const { data, isLoading } = usePublicFeatureFlagLocalStorage(SANA_REBRAND_ENABLED)
  if (isLoading) return false
  return Boolean(data)
}

function isVrShowcasePath(pathname: string) {
  return pathname === '/vr' || pathname.startsWith('/vr/')
}

/**
 * Boolean read of `sana_rebrand_enabled`.
 *
 * Resolution order: `?vr_panel=` → localStorage override on `/vr` only (and only
 * when the tenant flag is ON) → tenant feature flag.
 *
 * The profile-menu switch that used to write that override is gone. Product pages
 * follow the tenant flag and clear a leftover value so an old "off" cannot pin
 * the browser to the legacy theme. The `/vr` showcase toggle still uses it.
 *
 * Tests mock this hook to `true` globally (jestSetupFilesAfterEnv). Override
 * per-test when verifying legacy-mode behavior.
 *
 * TODO: Remove after `sana_rebrand_enabled` is retired.
 */
export function useIsSanaRebrandEnabled(): boolean {
  const vrOverride = useVrPanelOverride()
  const devOverride = useDevOverride()
  const pathname = typeof window === 'undefined' ? '' : window.location.pathname
  const onVrShowcase = isVrShowcasePath(pathname)
  const { data, isLoading } = usePublicFeatureFlagLocalStorage(SANA_REBRAND_ENABLED, {
    enabled: vrOverride === null
  })

  useEffect(() => {
    if (onVrShowcase || typeof window === 'undefined') return
    if (window.localStorage.getItem(SANA_REBRAND_DEV_OVERRIDE_KEY) == null) return
    setSanaRebrandDevOverride(null)
  }, [onVrShowcase])

  if (vrOverride !== null) return vrOverride
  if (isLoading) return false
  const flagOn = Boolean(data)
  // Dev override can only dial back (or re-assert) rebrand while the tenant flag is on.
  if (!flagOn) return false
  if (onVrShowcase && devOverride !== null) return devOverride
  return true
}

/**
 * Syncs `data-canvas-theme` on `<body>` with {@link useIsSanaRebrandEnabled}.
 * Must run under QueryClientProvider. Wired from `GlobalCSSVariables`
 * (Next `_app` + legacy `renderWorkdayTheme` via legacy_shims).
 *
 * TODO: Remove after `sana_rebrand_enabled` is retired.
 */
export function useAddCanvasThemeBodyAttribute() {
  const isRebrand = useIsSanaRebrandEnabled()

  useEffect(() => {
    if (isRebrand) {
      document.body.dataset.canvasTheme = 'true'
    } else {
      delete document.body.dataset.canvasTheme
    }
  }, [isRebrand])
}
